import type { Session } from '@supabase/supabase-js';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import { useAuthStore } from '../../../../auth/store/useAuthStore';
import { logBusinessAuditEvent } from '../../../../shell/audit/auditLogs';
import { captureOpsFailure } from '../../../../shared/sentry/sentryOps';
import {
  getCatalogRamSnapshotCache,
  patchWebCatalogStockInCache,
  patchWebCatalogStockTransferInCache,
} from '../../../../catalog/infra/catalogRamSnapshotCache';
import { findStockInWebCache } from './stockAdjustmentsRead';
import type { TransferRow, TransferStatus } from '../../domain/stockTypes';
// #1549: los lotes se mueven server-side en apply_stock_transfer_event — el
// origen consume (FEFO, vencidos de último recurso), el destino recibe lotes
// espejo con expires_at/unit_cost y reject crea lotes de retorno. No hace
// falta payload de lotes: el server deriva todo de las allocations.
import { StockTransferNotFoundError, StockTransferValidationError } from '../../domain/stockErrors';
import { assertProductIsInventoriable, assertVariantBelongsToProduct, resolveVariantLabel } from '../guards/stockInventoryGuards';
import { submitCommand } from '../../../../sync/core/submitCommand';
import { preflightCatalogSnapshotForRead } from '../../../../catalog/infra/catalogReadPreflight';
const validateTransferRequest = (params: {
  fromBranchId: string;
  toBranchId: string;
  quantity: number;
}) => {
  if (params.fromBranchId === params.toBranchId) {
    throw new StockTransferValidationError('La sucursal destino tiene que ser otra.');
  }
  if (params.quantity <= 0) {
    throw new StockTransferValidationError('La cantidad tiene que ser mayor a cero.');
  }
};

export const transferStock = async (
  params: {
    productId: string;
    fromBranchId: string;
    toBranchId: string;
    quantity: number;
    performedByUserId?: string | null;
    originDeviceId?: string | null;
    variantId?: string | null;
  },
  options?: { waitForReceive?: boolean }
) => {
  const { productId, fromBranchId, toBranchId, quantity, performedByUserId, originDeviceId, variantId } = params;
  const waitForReceive = options?.waitForReceive ?? false;

  validateTransferRequest({ fromBranchId, toBranchId, quantity });
  await assertProductIsInventoriable(productId);
  await assertVariantBelongsToProduct(variantId, productId);

  const fromStock = findStockInWebCache(productId, fromBranchId, variantId);
        if (!fromStock) {
          throw new StockTransferValidationError('No hay stock para transferir acá.');
        }
        if (fromStock.quantity < quantity) {
          throw new StockTransferValidationError('No alcanza el stock acá.');
        }

        const transferId = crypto.randomUUID();
        const performedAt = new Date().toISOString();
        const status: TransferStatus = waitForReceive ? 'sent' : 'completed';
        const unitCost = fromStock.avg_cost ?? 0;

        const result = await submitCommand({
          entity: 'stock_transfer',
          operation: 'insert',
          payload: {
            transferId,
            productId,
            fromBranchId,
            toBranchId,
            quantity,
            performedAt,
            status,
            unitCost,
            variantId: variantId ?? null,
          },
        });
        if (!result.applied) {
          const error = new Error(result.ingestError?.trim() || 'No se pudo transferir stock en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'inventory',
            op: 'stock.transfer',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: transferId,
            extra: { transferId, productId, fromBranchId, toBranchId },
          });
          throw error;
        }

        patchWebCatalogStockInCache(productId, fromBranchId, fromStock.quantity - quantity, fromStock.id, undefined, variantId);
        if (!waitForReceive) {
          const toStock = findStockInWebCache(productId, toBranchId, variantId);
          const toQty = (toStock?.quantity ?? 0) + quantity;
          const toAvg = toStock
            ? ((toStock.quantity * (toStock.avg_cost ?? 0)) + quantity * unitCost) / toQty
            : unitCost;
          patchWebCatalogStockInCache(productId, toBranchId, toQty, toStock?.id, toStock ? toAvg : unitCost, variantId);
        }
        patchWebCatalogStockTransferInCache(transferId, {
          product_id: productId,
          from_branch_id: fromBranchId,
          to_branch_id: toBranchId,
          quantity,
          unit_cost: unitCost,
          performed_at: performedAt,
          status,
          variant_id: variantId ?? null,
        });

        void logBusinessAuditEvent('stock.transfer.send', {
          transferId,
          productId,
          fromBranchId,
          toBranchId,
          quantity,
          status,
        });
        return transferId;
};

export const getPendingTransfersToReceive = async (
  toBranchId: string,
  opts?: { authSessionForRemoteFallback?: Session | null }
): Promise<TransferRow[]> => {
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  const session =
    opts?.authSessionForRemoteFallback !== undefined
      ? opts.authSessionForRemoteFallback
      : useAuthStore.getState().session;
  if (!session) {
    return [];
  }
  const existing = getCatalogRamSnapshotCache();
  if (!existing || existing.products.length === 0) {
    await preflightCatalogSnapshotForRead().catch(() => {});
  }
  const cached = getCatalogRamSnapshotCache();
  if (!cached) {
    return [];
  }
  const payload = {
    stock_transfers: cached.stockTransfers,
    products: cached.products,
  };
  const transfers = Array.isArray(payload.stock_transfers) ? payload.stock_transfers : [];
  const products = Array.isArray(payload.products) ? payload.products : [];
  const productById = new Map(products.map((product) => [product.id, product]));
  return transfers
    .filter((row) => row.to_branch_id === toBranchId && row.status === 'sent')
    .filter((row) => {
      const product = productById.get(row.product_id);
      return product != null && product.is_misc !== true && product.item_type !== 'service';
    })
    .map((row) => ({
      id: row.id,
      productId: row.product_id,
      fromBranchId: row.from_branch_id,
      toBranchId: row.to_branch_id,
      quantity: Number(row.quantity ?? 0),
      unitCost: row.unit_cost ?? null,
      performedAt: row.performed_at,
      status: 'sent' as TransferStatus,
      productName: productById.get(row.product_id)?.name ?? 'Producto',
      unitOfMeasure: normalizeUnitOfMeasure(productById.get(row.product_id)?.unit_of_measure),
      variantId: (row as { variant_id?: string | null }).variant_id ?? null,
      variantLabel: resolveVariantLabel((row as { variant_id?: string | null }).variant_id ?? null),
    }))
    .sort((a, b) => String(b.performedAt).localeCompare(String(a.performedAt)));
};

export const receiveTransfer = async (transferId: string, opts?: { performedByUserId?: string | null }): Promise<void> => {
  const performedByUserId = opts?.performedByUserId ?? null;

  const row = getCatalogRamSnapshotCache()?.stockTransfers.find((t) => t.id === transferId);
        if (!row || row.status !== 'sent') {
          throw new StockTransferNotFoundError();
        }
        await assertProductIsInventoriable(String(row.product_id));
        const transferVariantId = (row as { variant_id?: string | null }).variant_id ?? null;
        const performedAt = new Date().toISOString();
        const result = await submitCommand({
          entity: 'stock_transfer',
          operation: 'update',
          payload: {
            transferId,
            productId: row.product_id,
            fromBranchId: row.from_branch_id,
            toBranchId: row.to_branch_id,
            quantity: row.quantity,
            performedAt,
            status: 'received',
            unitCost: row.unit_cost ?? null,
            variantId: transferVariantId,
            performedByUserId,
          },
        });
        if (!result.applied) {
          const error = new Error(result.ingestError?.trim() || 'No se pudo recibir la transferencia en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'inventory',
            op: 'stock.transfer',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: transferId,
            extra: { transferId, status: 'received', performedByUserId },
          });
          throw error;
        }
        const toStock = findStockInWebCache(String(row.product_id), String(row.to_branch_id), transferVariantId);
        const unitCost = row.unit_cost ?? toStock?.avg_cost ?? 0;
        const toQty = (toStock?.quantity ?? 0) + Number(row.quantity ?? 0);
        patchWebCatalogStockInCache(String(row.product_id), String(row.to_branch_id), toQty, toStock?.id, unitCost, transferVariantId);
        patchWebCatalogStockTransferInCache(transferId, { status: 'received', performed_at: performedAt });
        void logBusinessAuditEvent('stock.transfer.receive', {
          transferId,
          productId: row.product_id,
          fromBranchId: row.from_branch_id,
          toBranchId: row.to_branch_id,
          quantity: row.quantity,
          performedByUserId,
        });
        return;
};

export const rejectTransfer = async (transferId: string, opts?: { performedByUserId?: string | null }): Promise<void> => {
  const performedByUserId = opts?.performedByUserId ?? null;

  const row = getCatalogRamSnapshotCache()?.stockTransfers.find((t) => t.id === transferId);
        if (!row || row.status !== 'sent') {
          throw new StockTransferNotFoundError();
        }
        await assertProductIsInventoriable(String(row.product_id));
        const transferVariantId = (row as { variant_id?: string | null }).variant_id ?? null;
        const performedAt = new Date().toISOString();
        const result = await submitCommand({
          entity: 'stock_transfer',
          operation: 'update',
          payload: {
            transferId,
            productId: row.product_id,
            fromBranchId: row.from_branch_id,
            toBranchId: row.to_branch_id,
            quantity: row.quantity,
            performedAt,
            status: 'rejected',
            unitCost: row.unit_cost ?? null,
            variantId: transferVariantId,
            performedByUserId,
          },
        });
        if (!result.applied) {
          const error = new Error(result.ingestError?.trim() || 'No se pudo rechazar la transferencia en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'inventory',
            op: 'stock.transfer',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: transferId,
            extra: { transferId, status: 'rejected', performedByUserId },
          });
          throw error;
        }
        const fromStock = findStockInWebCache(String(row.product_id), String(row.from_branch_id), transferVariantId);
        if (fromStock) {
          patchWebCatalogStockInCache(
            String(row.product_id),
            String(row.from_branch_id),
            fromStock.quantity + Number(row.quantity ?? 0),
            fromStock.id,
            undefined,
            transferVariantId,
          );
        }
        patchWebCatalogStockTransferInCache(transferId, { status: 'rejected', performed_at: performedAt });
        void logBusinessAuditEvent('stock.transfer.reject', {
          transferId,
          productId: row.product_id,
          fromBranchId: row.from_branch_id,
          toBranchId: row.to_branch_id,
          quantity: row.quantity,
          performedByUserId,
        });
        return;
};
